import { useOrgLockState } from '@/hooks/useOrgLockState';
import { useOpenAdminPanel } from '@/hooks/useOpenAdminPanel';

interface Props {
  /** The conversation's organization — drives the role-specific copy. */
  organizationId: string;
}

/**
 * Replaces the chat message composer when the conversation's org is paused
 * (PR 6). History stays visible and Firestore reads keep working — only new
 * messages are off. Copy varies by the viewer's role in THAT org. Never says
 * "suspended".
 */
export default function ChatComposerLock({ organizationId }: Props) {
  const { lockInfoFor } = useOrgLockState();
  const { openAdminPanel, opening } = useOpenAdminPanel();
  const info = lockInfoFor(organizationId);
  if (!info) return null;

  const churchAdmin = info.isAdmin && !info.isPersonal;
  const personalAdmin = info.isAdmin && info.isPersonal;

  let heading: string;
  let body: string;
  let link: { label: string } | null = null;

  if (churchAdmin) {
    heading = `${info.orgName} is paused.`;
    body = 'Your church’s plan needs attention — update billing to restore access for everyone.';
    link = { label: 'Update plan in Admin Panel →' };
  } else if (personalAdmin) {
    heading = `${info.orgName} is paused.`;
    body =
      'Your plan is billed through the App Store — open Flock on your iPhone or iPad to change it, or remove extra members.';
    link = { label: 'View plan in Admin Panel →' };
  } else {
    heading = 'This group is paused.';
    body =
      'You can read everything, but new messages are off until your admin restores the plan.';
  }

  // Approved mockup (rev 3): white bar, navy text — the amber treatment is
  // reserved for the capacity-window banner.
  return (
    <div className="border-t border-flock-gray-200 bg-white px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-lg leading-none" aria-hidden="true">
          🕊️
        </span>
        <div className="min-w-0 flex-1 text-sm text-flock-navy-600">
          <span className="font-bold text-flock-navy-800">{heading}</span> {body}
        </div>
        {link && (
          <button
            onClick={() => openAdminPanel()}
            disabled={opening}
            className="min-h-touch shrink-0 self-center whitespace-nowrap rounded-lg px-2 py-1 text-sm font-bold text-flock-navy-600 underline underline-offset-2 transition hover:text-flock-navy-800 disabled:opacity-60"
          >
            {opening ? 'Opening…' : link.label}
          </button>
        )}
      </div>
    </div>
  );
}
